/// <amd-module name="Test/TestResultsView/Components/TestResultsView" />
import * as React from "react";
import { FeatureFlagConstants, IViewContextData, ViewContext, ViewContextStatus } from "Test/common-util/Common";
import { DataProviderErrorCodes, ITestResultTreeData} from "Test/common-util/Contracts/ViewContracts";
import * as Resources from "Test/common-util/Resources";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { ITestHubSignalRSync, ITestSignalRHubService } from "Test/TestReportingSignalR/TestHub";
import { TestResultsGridActionsHub, TestResultsLeftViewActionHub } from "Test/TestResultsGrid/Actions/TestResultsGridActionHub";
import { TestResultsListViewActionCreator } from "Test/TestResultsGrid/Actions/TestResultsGridActionsCreator";
import { TestResultsLeftView } from "Test/TestResultsGrid/Components/TestResultLeftView";
import { TestResultsStore } from "Test/TestResultsGrid/Store/TestResultsTreeStore";
import {TestResultsViewActionHub, ITestResultViewDetails, DetailsPaneMode} from "Test/TestResultsView/Actions/TestResultsViewActionHub";
import * as TestResultViewResources from "Test/TestResultsView/Resources";
import { ITestResultsViewState, TestResultsViewStore } from "Test/TestResultsView/Store/TestResultsViewStore";
import { TestResultsReportActionsHub } from "Test/TestSummary/Actions/TestResultsReportActionsHub";
import { Summary } from "Test/TestSummary/Components/Summary";
import { ITfsPageService } from "Tfs/Platform/Page";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IQueryParameter, Uri } from "VSS/Platform/Util/Url";
import { Button } from "VSSUI/Button";
import { ISplitterProps, Splitter, SplitterElementPosition, SplitterDirection } from "VSSUI/Components/Splitter";
import { Image } from "VSSUI/Image";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";

export interface ITestResultsBaseProps extends IVssComponentProperties {
    artifactData: IViewContextData;
    widthAvailableToRender: number | undefined;
}

const LoadingComponent = () => <Spinner size={SpinnerSize.large} className="result-detail-panel overlay-right-section" />;

export class TestResultsView extends VssComponent<ITestResultsBaseProps, ITestResultsViewState, IVssComponentContext> {
    private static readonly c_resultIdKey = "resultId";
    private static readonly c_runIdKey = "runId";
    private static readonly c_paneViewKey = "paneView";

    private _testResultsLeftViewActionHub: TestResultsLeftViewActionHub;
    private _store: TestResultsViewStore;
    private _summaryActionHub: TestResultsReportActionsHub;
    private _testSignalRHubNotificationSync: TestSignalRHubNotificationsSync;
    private _testHub: ITestSignalRHubService | undefined;
    private _historyService: IVssHistoryService;
    private _testResultViewActionHub: TestResultsViewActionHub;
    private _gridStore: TestResultsStore;
    private _gridActionCreator: TestResultsListViewActionCreator;
    private _gridActionHub: TestResultsGridActionsHub;
    private readonly resultsNotAvailableImageResourceName: string;
    private _pageStateHistoryService: IPageStateHistoryService;
    private _isReleaseFlow: boolean;
    private _exitOrEnterFullScreenButtonRef: any;
    private _resultsContainerRef: any;

    constructor(props: ITestResultsBaseProps, context: IVssComponentContext) {
        super(props, context);

        this._exitOrEnterFullScreenButtonRef = React.createRef<Button>();
        this._summaryActionHub = new TestResultsReportActionsHub();
        this._testResultViewActionHub = new TestResultsViewActionHub();
        this._testResultsLeftViewActionHub = new TestResultsLeftViewActionHub();
        this._store = new TestResultsViewStore("TestResultsView", this._summaryActionHub, this._testResultViewActionHub);
        this.state = this._store.getState();
        if (this.props.artifactData.contributionSource === "ms.vss-test-web.test-result-in-release-environment-editor-tab") {
            this._isReleaseFlow = true;
        }
        this._pageStateHistoryService = context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this._testSignalRHubNotificationSync = new TestSignalRHubNotificationsSync(
            this._summaryActionHub,
            this._testResultsLeftViewActionHub,
            context.pageContext,
            this.props.artifactData
        );
        this._historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.resultsNotAvailableImageResourceName = getAssetLocation(
            this.context.pageContext,
            "ms.vss-test-web/common-content/waiting-for-results.png"
        );
        this._gridActionHub = new TestResultsGridActionsHub();
        this._gridStore = new TestResultsStore({
            actionHub: this._gridActionHub,
            artifactData: this.props.artifactData,
            pageContext: context.pageContext
        });
        this._gridActionCreator = new TestResultsListViewActionCreator(
            this._gridActionHub,
            this._gridStore,
            this.props.artifactData,
            context.pageContext
        );

        this.watchForTestResultChanges();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._handleStoreChange);
        const getState = this._historyService.getState();
        const resultId = getState[TestResultsView.c_resultIdKey];
        const runId = getState[TestResultsView.c_runIdKey];
        const paneView = getState[TestResultsView.c_paneViewKey];
        if (runId && +runId > 0) {
            const testResultViewDetails: ITestResultViewDetails = {
                runId: +runId,
                resultId: resultId != undefined ? +resultId : 0,
                paneView: paneView,
                isFullscreen: !isFeatureFlagEnabled(
                    this.context.pageContext,
                    FeatureFlagConstants.CTUrlSharing,
                    false
                )
            };
            this._testResultViewActionHub.openTestResultDetailsView.invoke(testResultViewDetails);
            const telemetryData: { [key: string]: any } = {};
            telemetryData["runId"] = runId;
            if (resultId) { telemetryData["resultId"] = resultId; }
            if (paneView) { telemetryData["paneView"] = paneView; }
            Telemetry.publishEvent(
                this.context.pageContext,
                this.props.artifactData,
                TelemetryConstants.featureTestTab_DetailsDirectLink,
                telemetryData
            );
        }

        // Calling setState explicitly as there can be child events called before the listener started.
        this.setState(this._store.getState());
    }

    public componentDidUpdate(): void {
        if (this.props.artifactData.status === ViewContextStatus.InProgress) {
            this.watchForTestResultChanges();
        } else {
            this.unwatchForTestResultChanges();
        }
    }

    public componentWillUnmount(): void {
        if (!this._isReleaseFlow) {
            if (this._pageStateHistoryService) {
                this._pageStateHistoryService.queueValueChange("runId", "", HistoryBehavior.replace);
                this._pageStateHistoryService.queueValueChange("resultId", "", HistoryBehavior.replace);
                this._pageStateHistoryService.queueValueChange("paneView", "", HistoryBehavior.replace);
            }
        } else {
            this.resetAndUpdateNavigationWithCurrentUrl();
        }
        this.unwatchForTestResultChanges();
        this._store.removeChangedListener(this._handleStoreChange);
    }

    static getDerivedStateFromProps(props: ITestResultsBaseProps, state: ITestResultsViewState)
    {
        let updatedState = state;
        if (updatedState.detailsPaneMode === DetailsPaneMode.HalfScreen || updatedState.detailsPaneMode === DetailsPaneMode.FullScreen) {
            if(!!props.widthAvailableToRender && (props.widthAvailableToRender <= TestResultsView._leftPaneMinWidth + TestResultsView._rightPaneMinWidth)) {
                updatedState.detailsPaneMode = DetailsPaneMode.MobileView;
                return updatedState
            }
        }
        return null;
    }

    public render(): JSX.Element {              
        const splitterProps: ISplitterProps = this.getSplitterProps();
        return (
            <>
                {this.state.isLoading && this._getLoadingSpinner()}
                {this.state.errorCode && this._getErrorMessageElement()}
                {
                    <div className="scroll-hidden test-results-container"
                        ref = {(testResultsViewContainer: HTMLDivElement) => (this._resultsContainerRef = testResultsViewContainer)}>
                        <Splitter {...splitterProps} />
                    </div>
                }
            </>
        );
    }

    private onRenderFarElement = () => {
        let fullScreenButton = null;
        if (this.state.detailsPaneMode === DetailsPaneMode.HalfScreen) {
            fullScreenButton = (
                <Button
                    ref={this._exitOrEnterFullScreenButtonRef}
                    subtle={true}
                    ariaLabel={Resources.EnterFullScreenModeText}
                    className="enter-full-screen"
                    iconProps={{ iconName: "FullScreen" }}
                    onClick={this._enterDetailsPanelFullScreen}
                    tooltipProps={{ showOnFocus: true, text: Resources.EnterFullScreenModeText }}
                />
            );
        } else if (this.state.detailsPaneMode === DetailsPaneMode.FullScreen) {
            fullScreenButton = (
                <Button
                    ref={this._exitOrEnterFullScreenButtonRef}
                    subtle={true}
                    ariaLabel={Resources.ExitFullScreenModeText}
                    className="exit-full-screen"
                    iconProps={{ iconName: "BackToWindow" }}
                    onClick={this._exitDetailsPanelFullScreen}
                    tooltipProps={{ showOnFocus: true, text: Resources.ExitFullScreenModeText }}
                />
            );
        }

        return (
            <div className="result-detail-panel overlay-right-section">
                <div className="overlay-panel-buttons-container">
                    {(this.state.detailsPaneMode === DetailsPaneMode.HalfScreen || this.state.detailsPaneMode === DetailsPaneMode.MobileView) && (
                        <Button
                            subtle={true}
                            ariaLabel={Resources.CloseDetailsPane}
                            iconProps={{ iconName: "Cancel" }}
                            onClick={this._closePanel}
                            tooltipProps={{ showOnFocus: true, text: Resources.CloseDetailsPane }}
                        />                    )}
                    {fullScreenButton}
                </div>
                {
                    <WrappedComponent
                        dependencies={["ms.vss-test-web.test-resultdetails", "ms.vss-test-web.test-result-details-tab-items"]}
                        wrappedType="test-result-details"
                        testResultTreeData={this.state.selectedTestResultTreeData}
                        runId={this.state.runId}
                        resultId={this.state.resultId}
                        paneView={this.state.paneView}
                        viewContext={this.props.artifactData}
                        isFullScreen={this.state.detailsPaneMode === DetailsPaneMode.FullScreen}
                        linkedStackTrace={true}
                        showHistory={true}
                        showHeader={true}
                        isNoConfigRuns={this.state.isNoConfigRuns}
                        loadingComponent={LoadingComponent}
                        onPivotChanged={(pivotKey: string) => {
                            this._testResultViewActionHub.onPivotChagnedInDetailsPane.invoke(pivotKey);
                        }}
                    />
                }
            </div>
        );
    };

    private _enterDetailsPanelFullScreen = () => {
        const state = this._store.getState();
        state.detailsPaneMode = DetailsPaneMode.FullScreen;
        this.setState(state);
        Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, TelemetryConstants.featureTestTab_DetailsPanelFullView, {
            count: 1
        });
    };

    private _exitDetailsPanelFullScreen = () => {
        const state = this._store.getState();
        state.detailsPaneMode = DetailsPaneMode.HalfScreen;
        this.setState(state);

        // Firing window resize event on exiting full mode of details panel
        // to adjust column width of Result Grid, because Grid rely on
        // it to resize column width.
        this._fireWindowResize();
    };

    // Fire window resize event
    private _fireWindowResize() {
        const event = document.createEvent("Event");
        event.initEvent("resize", false, true);
        window.dispatchEvent(event);
    }

    private _closePanel = () => {
        Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, TelemetryConstants.featureTestTab_DetailsPanelClosed, { count: 1 });
        const state = this._store.getState();
        state.detailsPaneMode = DetailsPaneMode.Off;
        state.paneView = "debug";
        state.runId = undefined;
        state.resultId = undefined;
        state.paneView = undefined;
        this.setState(state);
        if (this._pageStateHistoryService) {
            if(!this.state.onlyRunFetch){
                this._pageStateHistoryService.queueValueChange("runId", "", HistoryBehavior.replace);
            }
            this._pageStateHistoryService.queueValueChange("resultId", "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange("paneView", "", HistoryBehavior.replace);
        }

        // Firing window resize event on closing details panel
        // to adjust column width of Result Grid, because Grid rely on
        // it to resize column width.
        this._fireWindowResize();
    };

    private getSplitterProps(): ISplitterProps {
        if(this.state.detailsPaneMode == DetailsPaneMode.Off) {
            return {
                className: "horizontal test-overlay-panel-component right-fix",
                fixedElement: SplitterElementPosition.Far,
                onRenderNearElement: this.onRenderNearElement,
                nearElementClassName: "overview-panel scrollable-container",
                initialFixedSize: this._getInitialWidth(),
            } as ISplitterProps;
        }
        else if (this.state.detailsPaneMode >= DetailsPaneMode.FullScreen) {
            return {
                className: "horizontal test-overlay-panel-component right-fix",
                onRenderNearElement: this.onRenderNearElement,
                fixedElement: SplitterElementPosition.Far,
                onRenderFarElement: this.onRenderFarElement,
                nearElementClassName: "overview-panel scrollable-container hide-left-container",
                farElementClassName: "details-panel-fullView",
                minFixedSize: window.innerWidth,
                disabled:true,
            } as ISplitterProps;
        }
        else {
            return {
                className: "horizontal test-overlay-panel-component right-fix",
                fixedElement: SplitterElementPosition.Far,
                onRenderNearElement: this.onRenderNearElement,
                onRenderFarElement: this.onRenderFarElement,
                nearElementClassName: "overview-panel scrollable-container",
                farElementClassName: "details-panel",
                initialFixedSize: this._getInitialWidth(),
                minFixedSize: TestResultsView._rightPaneMinWidth,
                maxFixedSize: this._getMaxWidth()
            } as ISplitterProps;
        }
     
    }

    private _getInitialWidth(): number | undefined {
        if(window.innerWidth >= ScreenBreakpoints.large) {
            // at the begining we dont know the size witch splitter will get,
            // so we are subtracting vertical Project navigation with from windows width.
            return (window.innerWidth - this._projectNavigationWindowWidth)/2;
        }
        
        return undefined;  
    }

    private _getMaxWidth(): number | undefined {

        // We are not relying here on props.widthAvailableToRender, because when the first time we render this component the value of 
        // props.widthAvailableToRender is undefined.
        if(!!this._resultsContainerRef) {
            return Math.max(TestResultsView._rightPaneMinWidth, this._resultsContainerRef.clientWidth - TestResultsView._leftPaneMinWidth);
        }

        return undefined;
    } 

    private onRenderNearElement = () => {
        return (
            <div className="flex-column overlay-left-section">
                <div className="test-results-summary-view">
                    <Summary artifactData={this.props.artifactData} actionsHub={this._summaryActionHub} />
                </div>
                <TestResultsLeftView
                    artifactData={this.props.artifactData}
                    onGridRowClick={(item: ITestResultTreeData) => {
                        this._onGridRowClicked(item);
                    }}
                    testResultsLeftViewActionHub={this._testResultsLeftViewActionHub}
                    showGridComponents={!this.state.isLoading && this.state.errorCode ? false : true}
                    gridActionHub={this._gridActionHub}
                    gridStore={this._gridStore}
                    gridActionCreator={this._gridActionCreator}
                    isNoConfigRuns={this.state.isNoConfigRuns}
                    isOnlyRunFetch={this.state.onlyRunFetch}
                    isDetailsPanelOpened={this.state.detailsPaneMode !== DetailsPaneMode.Off}
                />
            </div>
        );
    };

    private _onGridRowClicked(item: ITestResultTreeData) {
        let mobileView = false;
        if(!!this.props.widthAvailableToRender && this.props.widthAvailableToRender < TestResultsView._leftPaneMinWidth + TestResultsView._rightPaneMinWidth) {
            mobileView = true;
        }

        this._testResultViewActionHub.onGridRowClicked.invoke({"testResultTreeData": item, "mobileView": mobileView});
    }

    private _getErrorMessageElement(): JSX.Element | null {
        if (this.props.artifactData.status === ViewContextStatus.InProgress && this._shouldShowWaitingForResultsImage(this.state.errorCode)) {
            return this._getInProgressLoadingComponent();
        } else {
            if (this.state.errorMessage) {
                return this._getEmptyResultsMessageBar(this.state.errorMessage);
            }
            return this._getEmptyResultsMessageBar(TestResultViewResources.BuildDoesNotHaveTestReports);
        }
    }

    private _getLoadingSpinner(): JSX.Element {
        return (
            <div className="flex flex-center justify-center test-results-loading-spinner-div">
                <Spinner
                    ariaLabel={Resources.LoadingTestResultDetailsLabel}
                    className="testresultsview-loading-spinner"
                    size={SpinnerSize.large}
                    label={Resources.LoadingTestResultDetailsLabel}
                />
            </div>
        );
    }

    private _shouldShowWaitingForResultsImage(errorCode: any): boolean {
        if (
            errorCode &&
            (errorCode === DataProviderErrorCodes.NoTestResultsInScenario || errorCode === DataProviderErrorCodes.ScenarioNotCompleted)
        ) {
            return true;
        } else {
            return false;
        }
    }

    private _getInProgressLoadingComponent(): JSX.Element {
        return (
            <div className={"flex flex-column flex-center justify-center waiting-for-results-div"}>
                <Image className={"waiting-for-results-image"} src={this.resultsNotAvailableImageResourceName} />
                <span className={"fontSizeLLL fontWeightNormal waiting-for-results-message"}>{TestResultViewResources.NoTestResultsMessage}</span>
            </div>
        );
    }

    private _getEmptyResultsMessageBar(message: string): JSX.Element {
        /* tslint:disable:react-no-dangerous-html */
        return (
            <div className={"empty-results-div fontSize flex"}>
                <span className={"empty-results-message-bar"} dangerouslySetInnerHTML={{ __html: message }} />
            </div>
        );
        /* tslint:enable:react-no-dangerous-html */
    }

    private _handleStoreChange = (): void => {
        const state = this._store.getState();
        this.setState(this._store.getState());
        if (this._isReleaseFlow) {
            this.updateNavigationWithCurrentUrl();
        }
        if (this._pageStateHistoryService) {
            this._pageStateHistoryService.queueValueChange("runId", state.runId != undefined ? `${state.runId}` : "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange(
                "resultId",
                state.resultId != undefined ? `${state.resultId}` : "",
                HistoryBehavior.replace
            );
            this._pageStateHistoryService.queueValueChange(
                "paneView",
                state.paneView != undefined ? `${state.paneView}` : "",
                HistoryBehavior.replace
            );
        }
    };

    private resetAndUpdateNavigationWithCurrentUrl(): void {
        const urlQueryParams = this.getUrlQueryparams(); // get the url before any reset to get current url.
        //  reset need to be done before setting current url param.
        if (this._pageStateHistoryService) {
            // reset the params which was added into history service by test tab
            this._pageStateHistoryService.queueValueChange("runId", "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange("resultId", "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange("paneView", "", HistoryBehavior.replace);

            // These are the param which we might have added into history service when we came here in function updateNavigationWithCurrentUrl
            // so we need to reset these values. if there is any new query param being added by release then we need to add here.
            this._pageStateHistoryService.queueValueChange("environmentId", "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange("extensionId", "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange("_a", "", HistoryBehavior.replace);
            this._pageStateHistoryService.queueValueChange("definitionId", "", HistoryBehavior.replace);
        }
        // Now set current Uri params to reflect currect param as per Url.
        urlQueryParams.forEach((param: IQueryParameter) => {
            if (param.name != "runId" && param.name != "resultId" && param.name != "paneView") {
                this._pageStateHistoryService.queueValueChange(param.name, param.value != undefined ? param.value : "", HistoryBehavior.replace);
            }
        });
    }

    private updateNavigationWithCurrentUrl(): void {
        const urlSearchParams = this.getUrlQueryparams();
        urlSearchParams.forEach((param: IQueryParameter) => {
            // dont reset our query params because in direct url, we should honor it to load full screen.
            if (param.name != "runId" && param.name != "resultId" && param.name != "paneView") {
                this._pageStateHistoryService.queueValueChange(param.name, param.value != undefined ? param.value : "", HistoryBehavior.replace);
            }
        });
    }

    private getUrlQueryparams(): IQueryParameter[] {
        const url = new Uri(window.location.href);
        const searchParams = url.queryParameters;
        return searchParams;
    }

    private async watchForTestResultChanges(): Promise<void> {
        if (this._testHub) {
            return;
        }

        const isInProgressRefreshEnabled = isFeatureFlagEnabled(
            this.context.pageContext,
            FeatureFlagConstants.InProgressRefreshUsingSignalREnabled,
            true
        );

        if (!isInProgressRefreshEnabled) {
            return;
        }

        if (this.props.artifactData.status !== ViewContextStatus.InProgress) {
            return;
        }

        try {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            await contributionService.getContributionAsync("ms.vss-test-web.test-reporting-signalr");

            this._testHub = this.context.pageContext.getService<ITestSignalRHubService>("ITestSignalRHub");

            if (this._testHub) {
                const projectId = this._getProjectId();
                // In release context we don't have the project Id available. Need to get it explicitly.
                if (!projectId) {
                    throw new Error("Unable to get projectId");
                }

                if (this.props.artifactData.viewContext === ViewContext.Build) {
                    const build = this.props.artifactData.data.mainData;
                    await this._testHub.initialize(this.context.pageContext, projectId, this._testSignalRHubNotificationSync);
                    await this._testHub.watchBuild(build.id);
                } else if (this.props.artifactData.viewContext === ViewContext.Release) {
                    const release = this.props.artifactData.data.mainData;
                    const environment = this.props.artifactData.data.subData.environment;
                    await this._testHub.initialize(this.context.pageContext, projectId, this._testSignalRHubNotificationSync);
                    await this._testHub.watchRelease(release.id, environment.id);
                }
                console.log("testHub: Successfully connected to Server")
            }
        } catch (error) {
            // Not throwing exceptions while handling signals.
            console.warn(`Error occurred while subscribing to testHub signals : ${error}`);
            const telemetryData: { [key: string]: any } = {};
            telemetryData[TelemetryConstants.errorOccurredDuringSignalRConnection] = error;
            Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, TelemetryConstants.featureTestTabSignalR, telemetryData);
        }
    }

    private _getProjectId(): string {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        if (tfsPageService) {
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData && tfsPageData.project) {
                return tfsPageData.project.id;
            }
        }
        return "";
    }

    private unwatchForTestResultChanges(): void {
        if (!this._testHub) {
            return;
        }
        if (this.props.artifactData.viewContext === ViewContext.Build) {
            this._testHub
                .unwatchBuild(this.props.artifactData.data.mainData.id)
                .then(() => {
                    if (this._testHub) {
                        this._testHub.dispose();
                        this._testHub = undefined;
                    }
                    console.log("testHub: Disconnected from server");
                })
                .catch((error: any) => {
                    console.error(`Error occurred while un-watching build : ${error}`);
                });
        } else if (this.props.artifactData.viewContext === ViewContext.Release) {
            const release = this.props.artifactData.data.mainData;
            const environment = this.props.artifactData.data.subData;
            this._testHub
                .unwatchRelease(release.id, environment.id)
                .then(() => {
                    if (this._testHub) {
                        this._testHub.dispose();
                        this._testHub = undefined;
                    }
                    console.log("testHub: Disconnected from server");
                })
                .catch((error: any) => {
                    console.error(`Error occurred while un-watching release : ${error}`);
                });
        }
    }

    private static _rightPaneMinWidth = 500;
    private static _leftPaneMinWidth = 500;
    private _projectNavigationWindowWidth = 260;
}

// Call backs when testHub receives notification from server
class TestSignalRHubNotificationsSync implements ITestHubSignalRSync {
    constructor(
        private _summaryActionsHub: TestResultsReportActionsHub,
        private _resultsViewActionHub: TestResultsLeftViewActionHub,
        private _pageContext: IVssPageContext,
        private _artifactData: IViewContextData
    ) {}

    public testRunStatsChangedForBuild(buildId: number): void {
        if (this._artifactData.data.mainData.id !== buildId) {
            return;
        }
        this._handleTestRunChangeSignal();
    }

    public testRunStatsChangedForRelease(releaseId: number, releaseEnvironmentId: number): void {
        if (this._artifactData.data.mainData.id !== releaseId || this._artifactData.data.subData.environment.id !== releaseEnvironmentId) {
            return;
        }
        this._handleTestRunChangeSignal();
    }

    private _handleTestRunChangeSignal() {
        console.log("testHub: Received signal from server");
        this._summaryActionsHub.refreshTestResultSummary.invoke(undefined);
        this._resultsViewActionHub.enableViewMoreTests.invoke(undefined);

        Telemetry.publishEvent(this._pageContext, this._artifactData, TelemetryConstants.featureTestTab_SignalRRefreshed, {});
    }
}
